import React, { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import './unifiedUI.scss'
import MusicOverlay from './musicOverlay.jsx'
import OverlayNavigation from './overlayNavigation.jsx'
import { useMusicPlayer } from './MusicPlayerContext.jsx'

/**
 * musicGallery Component
 * Music library gallery - displays album covers as image planes
 * Single level navigation with music player controls
 */
const musicGallery = ({ state, onRequestClose }) => {
  const [animationState, setAnimationState] = useState('closed')
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [focusedIndex, setFocusedIndex] = useState(null)
  
  const { 
    musicLibrary, 
    myMusicLibrary, 
    changeTrack, 
    playMyMusicTrack,
    togglePlayPause,
    nextTrack,
    previousTrack,
    changeVolume,
    isPlaying,
    volume
  } = useMusicPlayer()
  
  // Combine both libraries
  const allTracks = [...myMusicLibrary, ...musicLibrary]

  // Animation state machine
  useEffect(() => {
    if (state === 'open' && animationState === 'closed') {
      setAnimationState('opening')
      setCurrentItemIndex(0)
      
      const timer = setTimeout(() => {
        setAnimationState('open')
      }, 500)
      
      return () => clearTimeout(timer)
    } else if (state === 'closed' && animationState === 'open') {
      setAnimationState('closing')
      
      const timer = setTimeout(() => {
        setAnimationState('closed')
      }, 500)
      
      return () => clearTimeout(timer)
    }
  }, [state])

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key !== 'Escape' || animationState !== 'open') return
      
      event.preventDefault()
      event.stopPropagation()
      onRequestClose()
    }
    
    window.addEventListener('keydown', handleEscKey, true)
    return () => window.removeEventListener('keydown', handleEscKey, true)
  }, [animationState, onRequestClose])

  // Handle album click
  const handleAlbumClick = (index) => {
    setCurrentItemIndex(index)
    const track = allTracks[index]
    const isMyMusic = myMusicLibrary.some(t => t.id === track.id)
    
    if (isMyMusic) {
      const myMusicIndex = myMusicLibrary.findIndex(t => t.id === track.id)
      playMyMusicTrack(myMusicIndex, true)
    } else {
      const libraryIndex = musicLibrary.findIndex(t => t.id === track.id)
      changeTrack(libraryIndex, true)
    }
  }

  // Wheel navigation
  useEffect(() => {
    let isNavigating = false
    let navigationTimeout = null
    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0
    const cooldownDuration = 300

    const navigate = (direction) => {
      if (animationState !== 'open' || isNavigating) return

      let targetIndex = currentItemIndex + direction
      
      if (targetIndex < 0 || targetIndex >= allTracks.length) return

      isNavigating = true
      setCurrentItemIndex(targetIndex)

      if (navigationTimeout) clearTimeout(navigationTimeout)
      navigationTimeout = setTimeout(() => {
        isNavigating = false
      }, cooldownDuration)
    }

    const handleWheel = (event) => {
      const delta = event.deltaY
      if (Math.abs(delta) < 10) return

      const direction = delta > 0 ? 1 : -1
      const targetIndex = currentItemIndex + direction

      if (targetIndex >= 0 && targetIndex < allTracks.length) {
        event.preventDefault()
        navigate(direction)
      }
    }

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
      touchStartTime = Date.now()
    }

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const touchDuration = Date.now() - touchStartTime
      
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      
      // Only trigger if swipe is primarily horizontal and fast enough
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && touchDuration < 500) {
        const direction = deltaX > 0 ? -1 : 1 // Swipe right = previous, swipe left = next
        navigate(direction)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    
    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
      if (navigationTimeout) clearTimeout(navigationTimeout)
    }
  }, [animationState, currentItemIndex, allTracks.length])

  if (animationState === 'closed') return null

  const getClassName = () => {
    if (animationState === 'opening') return 'canvas-work-gallery opening'
    if (animationState === 'closing') return 'canvas-work-gallery closing'
    return 'canvas-work-gallery open'
  }

  return (
    <div className={getClassName()}>
      <Canvas
        linear
        flat
        camera={{
          fov: 50,
          near: 0.1,
          far: 1000,
          position: [0, 15, 0]
        }}
        dpr={[1, 2]}
        style={{ 
          width: '100vw', 
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, #5d728eff 0%, #687d99ff 100%)',
          backgroundSize: '100% 100%'
        }}
      >
        <MusicOverlay
          tracks={allTracks}
          currentItemIndex={currentItemIndex}
          onAlbumClick={handleAlbumClick}
          focusedIndex={focusedIndex}
          setFocusedIndex={setFocusedIndex}
        />
      </Canvas>

      {/* Noise overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        opacity: 0.4,
        mixBlendMode: 'overlay',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px'
      }} />

      {/* Navigation UI overlay - full viewport for controls */}
      <div className="canvas-ui-overlay">
        <OverlayNavigation
          breadcrumbPath={[{ label: 'MUSIC', level: 1 }]}
          onBreadcrumbClick={() => {}}
          currentItemIndex={currentItemIndex}
          maxItems={allTracks.length}
          onClose={onRequestClose}
        />

        {/* Music Player Controls (replacing step indicator) */}
        <div className="music-player-controls">
          <button className="nav-btn" onClick={previousTrack}>
            <span className="material-symbols-rounded">skip_previous</span>
          </button>
          
          <button className="nav-btn" onClick={togglePlayPause}>
            <span className="material-symbols-rounded">
              {isPlaying ? 'pause' : 'play_arrow'}
            </span>
          </button>
          
          <button className="nav-btn" onClick={nextTrack}>
            <span className="material-symbols-rounded">skip_next</span>
          </button>
          
          <div className="volume-control">
            <span className="material-symbols-rounded">volume_up</span>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.01" 
              value={volume} 
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              className="volume-slider"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default musicGallery
